import { useMemo } from 'react'

import {
  Button,
  ButtonGroup,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Tag,
  TagLabel,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Select } from 'chakra-react-select'
import { Formik } from 'formik'
import { LucideMoveRight } from 'lucide-react'
import * as Yup from 'yup'

import { useTagsContext } from '../ContextProvider/TagsContext'

const MergeTagsModal = ({ isOpen, onClose, sourceTag }) => {
  const { tags, mergeTag } = useTagsContext()

  const mergeValidationSchema = Yup.object().shape({
    targetTag: Yup.object().required('Target tag is required'),
  })

  const availableTags = useMemo(() => {
    return tags
      .filter(
        tag => tag.id !== sourceTag.id && tag.district === sourceTag.district
      )
      .map(tag => ({
        label: tag.label,
        value: tag,
      }))
  }, [tags, sourceTag])

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size='xl'>
      <ModalOverlay />
      <Formik
        initialValues={{
          targetTag: null,
        }}
        validationSchema={mergeValidationSchema}
        onSubmit={(values, { setSubmitting }) => {
          mergeTag(sourceTag, values.targetTag, () => {
            onClose()
          })
          setSubmitting(false)
        }}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          isSubmitting,
          handleSubmit,
        }) => (
          <ModalContent>
            <ModalHeader>Merge Tag: {sourceTag.label}</ModalHeader>
            <ModalBody>
              <VStack spacing={4} align='stretch'>
                <Text>
                  All casework with tag <b>{sourceTag.label}</b> will be merged
                  into the target tag, and <b>{sourceTag.label}</b> will be
                  permanently deleted.
                </Text>
                <Center direction='row' gap={3} w='full'>
                  <Tag>
                    <TagLabel textDecoration='line-through'>
                      {sourceTag.label}{' '}
                    </TagLabel>
                  </Tag>
                  <LucideMoveRight />
                  <Tag>
                    <TagLabel>{values.targetTag?.label || '...'}</TagLabel>
                  </Tag>
                </Center>
                <FormControl isInvalid={errors.targetTag && touched.targetTag}>
                  <FormLabel>Select target tag</FormLabel>
                  <Select
                    id='targetTag'
                    name='targetTag'
                    placeholder='Select a tag to merge into...'
                    isSearchable
                    options={availableTags}
                    useBasicStyles
                    value={
                      values.targetTag
                        ? {
                            label: values.targetTag.label,
                            value: values.targetTag,
                          }
                        : null
                    }
                    onChange={option => {
                      if (option?.value) {
                        setFieldValue('targetTag', option.value)
                      } else {
                        setFieldValue('targetTag', null)
                      }
                    }}
                  />
                  <FormErrorMessage>{errors.targetTag}</FormErrorMessage>
                </FormControl>
                {values.targetTag && (
                  <Text fontSize='sm' color='gray.600'>
                    This will affect <b>{sourceTag.count}</b> casework item(s).
                  </Text>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup w='100%'>
                <Button onClick={onClose} variant='ghost' colorScheme='gray'>
                  Cancel
                </Button>
                <Spacer />
                <Button
                  colorScheme='blue'
                  isLoading={isSubmitting}
                  onClick={handleSubmit}
                  isDisabled={!values.targetTag}
                >
                  Merge Tags
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        )}
      </Formik>
    </Modal>
  )
}

export default MergeTagsModal
